import { Injectable } from '@angular/core';
import { SideDrawerCoordinatorService } from '@netfoundry-ui/shared/services';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';

/** This drawer's id with the side-drawer coordinator (only one shell drawer open at a time). */
const DRAWER_ID = 'dial-logs';

export interface DialLogsDrawerData {
    resourceType?: 'endpoint' | 'service' | string;
    model?: { id?: string; name?: string };
    networkId?: string;
    networkGroupId?: string;
    /** Initial time range to pre-select when the drawer first opens (e.g. from the dashboard's
     * own range). One of the drawer's range ids: '24h' | '7d' | '30d'. */
    timeRange?: string;
    /** Which panel opened the drawer (e.g. 'top-usage'), so that panel can highlight itself
     * while its item is the one on show. */
    source?: string;
}

/** One entry in the list the drawer can page through (prev/next). */
export interface DialLogNavItem {
    id: string;
    name?: string;
}

/** Position of the open entity within its sibling list, for the header pager. */
export interface DialLogNavState {
    index: number; // -1 when the open entity isn't in the list
    total: number;
    hasPrev: boolean;
    hasNext: boolean;
}

/**
 * Shared state for the in-layout dial-logs drawer. The drawer host lives in the
 * cloud-ziti app shell (as a flex sibling of <main>) and any feature can open it
 * via this service. Width is persisted so a user's resize sticks across opens.
 */
@Injectable({ providedIn: 'root' })
export class DialLogsTimelineDrawerService {
    // Width bounds are the shared drawer's — every shell drawer uses one width (see the coordinator).
    static readonly MIN_WIDTH = SideDrawerCoordinatorService.MIN_WIDTH;
    static readonly MAX_WIDTH = SideDrawerCoordinatorService.MAX_WIDTH;

    private readonly _open = new BehaviorSubject<boolean>(false);
    private readonly _data = new BehaviorSubject<DialLogsDrawerData | null>(null);
    private readonly _siblings = new BehaviorSubject<DialLogNavItem[]>([]);

    readonly open$ = this._open.asObservable();
    readonly data$ = this._data.asObservable();
    /** Shared across all shell drawers so switching type keeps the panel width. */
    readonly width$: Observable<number>;

    /** Where the open entity sits in its sibling list — drives the header pager. */
    readonly nav$: Observable<DialLogNavState> = combineLatest([this._data, this._siblings]).pipe(
        map(([data, siblings]) => {
            const id = data?.model?.id;
            const index = id ? siblings.findIndex((s) => s.id === id) : -1;
            return {
                index,
                total: siblings.length,
                hasPrev: index > 0,
                hasNext: index >= 0 && index < siblings.length - 1,
            };
        })
    );

    constructor(private coordinator: SideDrawerCoordinatorService) {
        this.width$ = coordinator.width$;
        // Another shell drawer opened — stand down so only one is ever on screen.
        this.coordinator.opened$.pipe(filter((id) => id !== DRAWER_ID)).subscribe(() => this.close());
        // Leaving the page it was opened on — dismiss it so it never lingers onto the next route.
        this.coordinator.closeAll$.subscribe(() => this.close());
    }

    get isOpen(): boolean {
        return this._open.value;
    }

    /**
     * Open the drawer for one entity. `siblings` is the ordered, filtered list the
     * user is looking at (from the list page) so the header can page prev/next;
     * pass [] when there's nothing to page through.
     */
    open(data: DialLogsDrawerData, siblings: DialLogNavItem[] = []): void {
        this.coordinator.notifyOpened(DRAWER_ID);
        this._data.next(data);
        this._siblings.next(siblings);
        this._open.next(true);
    }

    /** Set/replace the sibling list without reopening (e.g. after an async fetch). */
    setSiblings(siblings: DialLogNavItem[]): void {
        this._siblings.next(siblings);
    }

    /** Step to the previous (-1) or next (1) entity in the sibling list. */
    navigate(direction: -1 | 1): void {
        const list = this._siblings.value;
        const id = this._data.value?.model?.id;
        const index = id ? list.findIndex((s) => s.id === id) : -1;
        const target = list[index + direction];
        if (index < 0 || !target) return;
        this._data.next({ ...this._data.value, model: { id: target.id, name: target.name } });
    }

    close(): void {
        this._open.next(false);
    }

    setWidth(px: number): void {
        this.coordinator.setWidth(px);
    }
}
